import { Link } from 'react-router-dom';
import PageHero from '../components/ui/PageHero';
import {
  LuLuggage, LuScanLine, LuBrainCircuit, LuTruck, LuPlane,
  LuRotateCcw, LuZap, LuScanEye, LuRadio, LuArrowLeftRight,
  LuMonitor, LuArrowRight, LuShieldCheck, LuCpu, LuInfo,
} from 'react-icons/lu';

const IMG = {
  hero:       'https://upload.wikimedia.org/wikipedia/commons/b/bd/BIAL_arrival_luggage_belt_3.jpg',
  checkin:    'https://t4.ftcdn.net/jpg/05/63/54/71/360_F_563547157_2LSshaDZUR5lHXIDfy1IQT3OzphATfNl.jpg',
  sorting:    'https://i.dailymail.co.uk/i/pix/2013/12/23/article-2528042-1A45D64A00000578-727_964x667.jpg',
  security:   'https://images.unsplash.com/photo-1540962351504-03099e0a754b?w=700&q=80&auto=format',
  controlRoom:'https://media.licdn.com/dms/image/sync/v2/D5627AQGNS2BrVkmalw/articleshare-shrink_800/articleshare-shrink_800/0/1711625213741?e=2147483647&v=beta&t=0jXb7an4-rj-KKV8cd0FB9dfiCp7PYBetvs-PLigES0',
  belt:       'https://p.turbosquid.com/ts-thumb/fo/1mM8iu/Y35MQl1b/airportconveyorbeltsystemwithbaggagesmb3dmodel004/jpg/1523397688/1920x1080/fit_q87/dd244b1c5f6c0bd34f0d4e299ced2c406d4c8e36/airportconveyorbeltsystemwithbaggagesmb3dmodel004.jpg',
  motor:      'https://mir-s3-cdn-cf.behance.net/project_modules/1400/597d4223811023.563292decffa2.jpg',
  sensor:     'https://skud.pro/wp-content/uploads/2025/07/e52fc5d3-8093-4809-9f28-8df2ed28b212.png',
  rfid:       'https://avatars.mds.yandex.net/i?id=bb737e1f79fc0c6d846494d6ae85c50f6f3e3e22-4097583-images-thumbs&n=13',
  sorter:     'https://i.dailymail.co.uk/i/pix/2013/12/23/article-2528042-1A45D64A00000578-727_964x667.jpg',
  control:    'https://media.licdn.com/dms/image/sync/v2/D5627AQGNS2BrVkmalw/articleshare-shrink_800/articleshare-shrink_800/0/1711625213741?e=2147483647&v=beta&t=0jXb7an4-rj-KKV8cd0FB9dfiCp7PYBetvs-PLigES0',
};

const STEPS = [
  { num:'01', icon:<LuLuggage size={22}/>,      color:'#7c3aed', bg:'#ede9fe', title:'Bag Drop',         desc:'Passenger checks in and a unique barcode or RFID tag is attached to the bag at the counter.' },
  { num:'02', icon:<LuScanLine size={22}/>,      color:'#0891b2', bg:'#cffafe', title:'Tag Scanning',     desc:`Multiple readers capture the bag's identity from multiple angles as it enters the belt system.` },
  { num:'03', icon:<LuBrainCircuit size={22}/>,  color:'#db2777', bg:'#fce7f3', title:'Smart Sorting',    desc:'The BMS routes the bag to the correct departure gate path in milliseconds using AI algorithms.' },
  { num:'04', icon:<LuTruck size={22}/>,         color:'#d97706', bg:'#fff7ed', title:'Transportation',   desc:'Electric motors drive belts through underground tunnels at speeds up to 10 m/s.' },
  { num:'05', icon:<LuPlane size={22}/>,         color:'#059669', bg:'#d1fae5', title:'Aircraft Loading', desc:'Bags arrive at the correct gate and are loaded into the cargo hold by ground handling teams.' },
];

const COMPONENTS = [
  { icon:<LuRotateCcw size={22}/>, img:IMG.belt,    title:'Conveyor Belt',   color:'#7c3aed', bg:'#ede9fe', desc:'Rubber or steel-reinforced surface that physically carries baggage along the defined route from check-in to gate.' },
  { icon:<LuZap size={22}/>,       img:IMG.motor,   title:'Motor System',    color:'#0891b2', bg:'#cffafe', desc:'Variable-speed electric drives and gearboxes powering belt movement, speed control, and directional changes.' },
  { icon:<LuScanEye size={22}/>,   img:IMG.sensor,  title:'Sensor Array',    color:'#db2777', bg:'#fce7f3', desc:'Optical, weight, and presence sensors detecting bag position, dimensions, and real-time status at every checkpoint.' },
  { icon:<LuRadio size={22}/>,     img:IMG.rfid,    title:'RFID / Barcode',  color:'#d97706', bg:'#fff7ed', desc:"Reads the bag's unique tag ID and links it to real-time passenger flight data in the central Baggage Management System." },
  // { icon:<LuArrowLeftRight size={22}/>, img:IMG.sorter, title:'Sorting Machine', color:'#059669', bg:'#d1fae5', desc:'High-speed divert units and crossbelt sorters that redirect bags to the correct downstream path — 3,000+/hr.' },
  // { icon:<LuMonitor size={22}/>,   img:IMG.control, title:'Control Room',    color:'#8b5cf6', bg:'#ede9fe', desc:'Central monitoring hub where operators oversee all belt operations in real time and handle emergencies.' },
];

const TECH_TAGS = [
  { icon:<LuZap size={13}/>,           label:'Electric Motors' },
  { icon:<LuScanEye size={13}/>,       label:'Optical Sensors' },
  { icon:<LuRotateCcw size={13}/>,     label:'Drive Rollers' },
  { icon:<LuMonitor size={13}/>,       label:'Control Systems' },
  { icon:<LuRadio size={13}/>,         label:'RFID / Barcode' },
  { icon:<LuBrainCircuit size={13}/>,  label:'AI Sorting' },
  { icon:<LuLuggage size={13}/>,       label:'Bag Detection' },
  { icon:<LuShieldCheck size={13}/>,   label:'Emergency Stops' },
  { icon:<LuCpu size={13}/>,           label:'Cloud BMS' },
  { icon:<LuTruck size={13}/>,         label:'IoT Network' },
];

function HowItWorks() {
  return (
    <div className="page-enter">
      <PageHero
        tag="Step by Step"
        title={<>How Does the System <span className="text-gradient">Work?</span></>}
        subtitle="From the moment you hand over your bag to when it appears on the carousel — a precise, fully automated journey of remarkable engineering."
        breadcrumb={[{ label:'How It Works' }]}
      >
        <div className="page-hero-img-card">
          <img src={IMG.hero} alt="Airport check-in conveyor belt" loading="lazy"/>
        </div>
      </PageHero>

      {/* ══════════════════════════════════════════
          5 STEPS
      ══════════════════════════════════════════ */}
      <section className="page-section bg-dark-2">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom:56 }}>
            <div className="section-tag">Process</div>
            <h2 className="section-title">
              5 Steps from Drop <span className="text-gradient">to Departure</span>
            </h2>
            <p className="section-sub" style={{ margin:'0 auto' }}>
              Every bag follows exactly this sequence — monitored and verified at each stage
              by sensors, cameras, and AI-controlled software.
            </p>
          </div>

          {/* Step cards */}
          <div style={{
            display:'grid',
            gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))',
            gap:16,
            position:'relative',
          }}>
            {/* connector line */}
            <div style={{
              position:'absolute', top:52, left:'8%', right:'8%', height:2,
              background:'linear-gradient(90deg, transparent, var(--primary), var(--accent-light), transparent)',
              pointerEvents:'none', zIndex:0,
            }}/>

            {STEPS.map((s,i) => (
              <div
                key={i}
                className={`reveal delay-${i+1}`}
                style={{
                  background:'var(--card-bg)',
                  border:'1px solid var(--border-light)',
                  borderRadius:20,
                  padding:'28px 20px 24px',
                  textAlign:'center',
                  position:'relative',
                  zIndex:1,
                  transition:'all .3s',
                  boxShadow:'var(--shadow-sm)',
                  cursor:'default',
                }}
                onMouseEnter={e=>{
                  e.currentTarget.style.transform='translateY(-8px)';
                  e.currentTarget.style.boxShadow='var(--shadow-lg)';
                  e.currentTarget.style.borderColor=s.color+'44';
                }}
                onMouseLeave={e=>{
                  e.currentTarget.style.transform='';
                  e.currentTarget.style.boxShadow='var(--shadow-sm)';
                  e.currentTarget.style.borderColor='var(--border-light)';
                }}
              >
                {/* number badge */}
                <div style={{
                  width:52, height:52, borderRadius:'50%',
                  background:`linear-gradient(135deg, ${s.color}, ${s.color}cc)`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontFamily:'var(--font-display)', fontSize:'1rem', fontWeight:800,
                  color:'#fff', margin:'0 auto 16px',
                  boxShadow:`0 0 0 6px var(--bg-3), 0 0 0 8px ${s.color}28`,
                }}>
                  {s.num}
                </div>

                {/* icon pill */}
                <div style={{
                  display:'inline-flex', alignItems:'center', justifyContent:'center',
                  width:40, height:40, borderRadius:10,
                  background:s.bg, color:s.color,
                  marginBottom:14, border:`1px solid ${s.color}22`,
                }}>
                  {s.icon}
                </div>

                <div style={{ fontFamily:'var(--font-display)', fontSize:'.95rem', fontWeight:700, color:'var(--text-2)', marginBottom:8 }}>
                  {s.title}
                </div>
                <div style={{ fontSize:'.8rem', color:'var(--text-muted)', lineHeight:1.7 }}>
                  {s.desc}
                </div>

                {/* bottom accent */}
                <div style={{
                  height:3, borderRadius:2, marginTop:20,
                  background:`linear-gradient(90deg, ${s.color}, ${s.color}44)`,
                }}/>
              </div>
            ))}
          </div>

        

          {/* Tech tags */}
          <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginTop:48, justifyContent:'center' }} className="reveal">
            {TECH_TAGS.map((t,i) => (
              <span key={i} className="tech-tag-chip">
                <span style={{ color:'var(--primary)' }}>{t.icon}</span>
                {t.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* ══════════════════════════════════════════
          COMPONENTS
      ══════════════════════════════════════════ */}
      <section className="page-section" id="components">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom:52 }}>
            <div className="section-tag">Hardware</div>
            <h2 className="section-title">
              Main System <span className="text-gradient">Components</span>
            </h2>
            <p className="section-sub" style={{ margin:'0 auto' }}>
              Six core hardware and software components work in harmony to deliver your bag
              reliably on every flight, every day.
            </p>
          </div>

          <div style={{
            display:'grid',
            gridTemplateColumns:'repeat(auto-fit, minmax(290px, 1fr))',
            gap:20,
          }}>
            {COMPONENTS.map((c,i) => (
              <div
                key={i}
                className={`reveal delay-${(i%4)+1}`}
                style={{
                  background:'var(--card-bg)',
                  border:'1px solid var(--border-light)',
                  borderRadius:20,
                  overflow:'hidden',
                  boxShadow:'var(--shadow-sm)',
                  transition:'all .3s',
                }}
                onMouseEnter={e=>{
                  e.currentTarget.style.transform='translateY(-6px)';
                  e.currentTarget.style.boxShadow='var(--shadow-lg)';
                  e.currentTarget.style.borderColor=c.color+'44';
                  const img = e.currentTarget.querySelector('img');
                  if(img) img.style.transform='scale(1.07)';
                  const icon = e.currentTarget.querySelector('.hiw-comp-icon');
                  if(icon){ icon.style.background=`linear-gradient(135deg, ${c.color}, ${c.color}bb)`; icon.style.color='#fff'; }
                }}
                onMouseLeave={e=>{
                  e.currentTarget.style.transform='';
                  e.currentTarget.style.boxShadow='var(--shadow-sm)';
                  e.currentTarget.style.borderColor='var(--border-light)';
                  const img = e.currentTarget.querySelector('img');
                  if(img) img.style.transform='';
                  const icon = e.currentTarget.querySelector('.hiw-comp-icon');
                  if(icon){ icon.style.background=c.bg; icon.style.color=c.color; }
                }}
              >
                {/* Image */}
                <div style={{ height:140, overflow:'hidden', position:'relative' }}>
                  <img
                    src={c.img} alt={c.title} loading="lazy"
                    style={{ width:'100%', height:'100%', objectFit:'cover', display:'block', transition:'transform .4s ease' }}
                  />
                  {/* colored top bar */}
                  <div style={{
                    position:'absolute', top:0, left:0, right:0, height:4,
                    background:`linear-gradient(90deg, ${c.color}, ${c.color}66)`,
                  }}/>
                </div>

                {/* Body */}
                <div style={{ padding:'20px 22px 24px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
                    <div
                      className="hiw-comp-icon"
                      style={{
                        width:44, height:44, borderRadius:11,
                        background:c.bg, color:c.color,
                        display:'flex', alignItems:'center', justifyContent:'center',
                        flexShrink:0, border:`1px solid ${c.color}22`,
                        transition:'all .3s',
                      }}
                    >
                      {c.icon}
                    </div>
                    <h3 style={{ fontFamily:'var(--font-display)', fontSize:'.98rem', fontWeight:700, color:'var(--text-2)', margin:0 }}>
                      {c.title}
                    </h3>
                  </div>
                  <p style={{ fontSize:'.84rem', color:'var(--text-muted)', lineHeight:1.72, margin:0 }}>
                    {c.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* ══════════════════════════════════════════
          FULL JOURNEY
      ══════════════════════════════════════════ */}
      <section className="page-section bg-dark-2">
        <div className="container">
          <div className="grid-2">
            {/* Left */}
            <div className="reveal-left">
              <div className="section-tag">Full Journey</div>
              <h2 className="section-title">The Complete <span className="text-gradient">Baggage Path</span></h2>
              <p style={{ color:'var(--text-3)', lineHeight:1.85, marginBottom:16 }}>
                Every bag follows a precisely choreographed path, monitored at every metre
                by sensors and software systems working in concert.
              </p>
              <p style={{ color:'var(--text-3)', lineHeight:1.85, marginBottom:24 }}>
                The journey spans multiple underground conveyor levels, X-ray tunnels,
                automatic divert gates, and the correct flight container — all completed
                in under 20 minutes from check-in to aircraft hold.
              </p>

              {/* image strip */}
              <div className="hiw-img-strip">
                <div className="hiw-img-tile">
                  <img src={IMG.sorting} alt="Automated bag sorting system" loading="lazy"/>
                  <span>Sorting Hall</span>
                </div>
                <div className="hiw-img-tile">
                  <img src={IMG.controlRoom} alt="Baggage control room" loading="lazy"/>
                  <span>Control Room</span>
                </div>
              </div>

              <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginTop:28 }}>
                <Link to="/technology" className="btn btn-primary">
                  Explore Technology <LuArrowRight size={16}/>
                </Link>
                <Link to="/safety" className="btn btn-outline">
                  <LuShieldCheck size={16}/> Safety Systems
                </Link>
              </div>
            </div>

            {/* Right */}
            <div className="reveal-right">
              <div className="hiw-main-img">
                <img src={IMG.checkin} alt="Airport baggage conveyor tunnel" loading="lazy"/>
                <div className="hiw-main-img-caption">
                  <LuInfo size={14}/>
                  Underground baggage tunnel at a major international airport
                </div>
              </div>

              {/* Step list */}
              <div style={{ display:'flex', flexDirection:'column', gap:0, marginTop:20 }}>
                {STEPS.map((s,i) => (
                  <div key={i}>
                    <div
                      className="hiw-flow-row"
                      onMouseEnter={e=>{
                        e.currentTarget.style.borderColor=s.color+'55';
                        e.currentTarget.style.background=s.bg;
                      }}
                      onMouseLeave={e=>{
                        e.currentTarget.style.borderColor='var(--border-light)';
                        e.currentTarget.style.background='var(--card-bg)';
                      }}
                    >
                      <div className="hiw-flow-num" style={{ background:`linear-gradient(135deg, ${s.color}, ${s.color}bb)` }}>
                        {s.num}
                      </div>
                      <span style={{ color:s.color }}>{s.icon}</span>
                      <span style={{ fontSize:'.88rem', fontWeight:600, color:'var(--text-2)' }}>{s.title}</span>
                    </div>
                    {i < STEPS.length-1 && (
                      <div className="hiw-flow-connector" style={{ background:`linear-gradient(to bottom, ${STEPS[i].color}, ${STEPS[i+1].color})` }}/>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HowItWorks;
